import { useRef, useEffect } from "react";
import { Bookmark, Palette, Share2, SlidersHorizontal, Square } from "lucide-react";
import { useSuperformula } from "./SuperformulaContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { SUPERFORMULA_PRESETS } from "../../utils/superformula";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function SuperformulaControls() {
    const {
        m,
        a,
        b,
        n1,
        n2,
        n3,
        size,
        samples,
        fillColor,
        strokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        exportBackground,
        setM,
        setA,
        setB,
        setN1,
        setN2,
        setN3,
        setSize,
        setSamples,
        setFillColor,
        setStrokeColor,
        setBgColor,
        setStrokeWidth,
        setFillShapes,
        setExportBackground,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
        canExport,
        buildExportSvgString,
        handleDownload,
    } = useSuperformula();

    const fillColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const strokeColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const bgColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (fillColorTimeout.current)
                clearTimeout(fillColorTimeout.current);
            if (strokeColorTimeout.current)
                clearTimeout(strokeColorTimeout.current);
            if (bgColorTimeout.current) clearTimeout(bgColorTimeout.current);
        };
    }, []);

    const handleFillPicker = (v: string) => {
        if (fillColorTimeout.current) clearTimeout(fillColorTimeout.current);
        fillColorTimeout.current = setTimeout(() => setFillColor(v), 150);
    };
    const handleStrokePicker = (v: string) => {
        if (strokeColorTimeout.current)
            clearTimeout(strokeColorTimeout.current);
        strokeColorTimeout.current = setTimeout(() => setStrokeColor(v), 150);
    };
    const handleBgPicker = (v: string) => {
        if (bgColorTimeout.current) clearTimeout(bgColorTimeout.current);
        bgColorTimeout.current = setTimeout(() => setBgColor(v), 150);
    };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    return (
        <div className="panel-controls superformula-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Superformula</h2>
                    <ToolDescriptionPopover dialogLabel="Superformula tool description">
                        <p>
                            Johan Gielis&apos;s polar formula generates
                            thousands of organic outlines - flowers, stars,
                            cells, leaves - from six parameters. Adjust the
                            sliders or pick a preset to explore the space.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download SVG
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="superformula"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Named presets" icon={Bookmark}>
                    <div
                        style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                        }}
                    >
                        {SUPERFORMULA_PRESETS.map((preset) => (
                            <button
                                key={preset.name}
                                type="button"
                                className="zoom-button"
                                style={{ minWidth: "72px" }}
                                onClick={() =>
                                    applyPreset(
                                        preset as unknown as Record<
                                            string,
                                            unknown
                                        >,
                                    )
                                }
                            >
                                {preset.name}
                            </button>
                        ))}
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Parameters"
                    icon={SlidersHorizontal}
                >
                    <div className="field-row">
                        <label className="field-label">
                            m - symmetry order
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                max={20}
                                step={0.5}
                                value={m}
                                onChange={handleFloat(setM, 0.5, 20)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            a - x-axis scale
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={10}
                                step={0.1}
                                value={a}
                                onChange={handleFloat(setA, 0.1, 10)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            b - y-axis scale
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={10}
                                step={0.1}
                                value={b}
                                onChange={handleFloat(setB, 0.1, 10)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            n1 - overall pinching
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={20}
                                step={0.01}
                                value={n1}
                                onChange={handleFloat(setN1, 0.01, 20)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            n2 - cos exponent
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={20}
                                step={0.01}
                                value={n2}
                                onChange={handleFloat(setN2, 0.01, 20)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            n3 - sin exponent
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={20}
                                step={0.01}
                                value={n3}
                                onChange={handleFloat(setN3, 0.01, 20)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Canvas" icon={Square}>
                    <div className="field-row">
                        <label className="field-label">
                            Size (SVG units)
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={4000}
                                step={50}
                                value={size}
                                onChange={handleInt(setSize, 50)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Samples
                            <DebouncedInput
                                type="number"
                                min={64}
                                max={2048}
                                step={64}
                                value={samples}
                                onChange={handleInt(setSamples, 64)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            checked={fillShapes}
                            onChange={setFillShapes}
                            label="Fill shape"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Fill color
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={fillColor}
                                    onChange={(e) =>
                                        handleFillPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={fillColor}
                                    onChange={(v) => setFillColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={strokeColor}
                                    onChange={(e) =>
                                        handleStrokePicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={strokeColor}
                                    onChange={(v) => setStrokeColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Background color
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={bgColor}
                                    onChange={(e) =>
                                        handleBgPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={bgColor}
                                    onChange={(v) => setBgColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={exportBackground}
                            onChange={setExportBackground}
                            label="Include background in export"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={50}
                                step={0.5}
                                value={strokeWidth}
                                onChange={handleFloat(setStrokeWidth, 0, 50)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "superformula-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["superformula"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
